import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { actionCRM, postCRM, resetCRMStatus } from './CRMSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { Alert, IconButton, InputAdornment, OutlinedInput, Slide, SlideProps, Snackbar } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Button, Container, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './CRM.css';

type props = {
    visible: boolean;
    onClose: () => void;
    customerID: string;
};

const CRMEntryModal = ({ visible, onClose, customerID }: props) => {
    const noteRef = useRef(null);
    const custRef = useRef(null);
    const [createFollowUp, setCreateFollowUp] = useState<boolean>(false);
    const [date, setDate] = useState<Date | null>(new Date());
    const [custID, setCustID] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [custErrorMessage, setCustErrorMessage] = useState<boolean>(false);
    const [noteErrorMessage, setNoteErrorMessage] = useState<boolean>(false);
    const errMsg = useAppSelector((state) => state.crm.errMsg);
    const dispatch = useAppDispatch();
    const inputAccessoryViewID = '123465';

    useEffect(() => {
        setCustID(customerID);
    }, []);

    const handleSubmit = async () => {
        
        if (custID && createFollowUp && date && note) {
            console.log(`CRM ENTRY CALENDAR: ${note}, ${date}, ${custID}`)

            try {
                await dispatch(actionCRM({custID, note})).unwrap();

                setCustID('');
                setNote('');
                setCustErrorMessage(false);
                setNoteErrorMessage(false);
                setCreateFollowUp(false);
                setDate(new Date());

            } catch (err: any) {
                console.error('CRM ACTION ERROR:', err)
            }
        } else if (!createFollowUp && custID && note) {
            try {
                await dispatch(postCRM({custID, note})).unwrap();

                dispatch(resetCRMStatus());
                setCustID('');
                setNote('');
                setCustErrorMessage(false);
                setNoteErrorMessage(false);
                setCreateFollowUp(false);
                setDate(new Date());
            } catch (e: any) {
                console.error('POST CRM ERROR', e.message);
            }

        } else {  
            if (!custID) {
                setCustErrorMessage(true);
                return;
            }

            if (!note) {
                setNoteErrorMessage(true);
                return;
            }

            setCustID('');
            setNote('');
            setCustErrorMessage(false);
            setNoteErrorMessage(false);
            setCreateFollowUp(false);
            setDate(new Date());
        }
    };

    const handleCRMAction = async () => {
        setCreateFollowUp(!createFollowUp);
        logEvent(analytics, 'crm_action_web', {
            name: 'CRM Action checkmark',
            description: 'User pressed CRM Follow-up'
        });
    };

    return (
        <Modal
            isOpen={visible}
            onClosed={onClose}
        >
            <ModalHeader
                className='bg-primary d-flex justify-content-center'
                style={{ color: 'white', }}
            >
                <Button 
                    title='Close'
                    onClick={onClose}
                    className="bg-transparent border-0 fw-bold text-white position-absolute start-0 ms-2"
                    unmountOnClose
                >
                    Close
                </Button>
                <span 
                    style={{ color: 'white', fontWeight: 'bold', fontSize: 18, }}
                >
                    CRM Entry
                </span>
            </ModalHeader>
            <ModalBody 
                // className='pt-4'
            >
                <div>
                    <OutlinedInput 
                        id='custId'
                        style={{ width: '100%' }}
                        value={custID}
                        className='bg-white shadow-sm'
                        onChange={(e) => setCustID(e.target.value)}
                        placeholder='Customer ID'
                        required
                        error={custErrorMessage}
                    />
                    <div className='text-start'>
                        {custErrorMessage && <span style={{ color: 'red', left: 10, }}>This field is required!</span>}
                        {errMsg && <span style={{ color: 'red', left: 10 }}>{errMsg}</span>}
                    </div>
                </div>
                <div 
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginBlock: 10,
                    }}
                >
                    <Input
                        id="followUp"
                        type="checkbox"
                        className="mx-2 color-primary"
                        color="primary"
                        checked={createFollowUp}
                        onChange={handleCRMAction}
                    />
                    <Label 
                        check
                        for="followUp"
                    >
                        Create Action
                    </Label>
                    {createFollowUp && 
                        <div style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 20 }}>
                            <span>Follow-Up Date: </span>
                            <DatePicker
                                showIcon
                                toggleCalendarOnIconClick
                                selected={date} 
                                onChange={(date: Date | null) => setDate(date)}
                                icon='fa fa-calendar'
                            />
                        </div>
                    }
                </div>
                <div>
                    <OutlinedInput 
                        ref={noteRef}
                        type='text'
                        style={{ width: '100%', minHeight: 100 }}
                        value={note}
                        className='bg-white shadow-sm'
                        onChange={(e) => setNote(e.target.value)}
                        placeholder='CRM Note'
                        multiline
                        error={noteErrorMessage}
                        required
                        sx={{
                            alignItems: 'flex-start',
                            '& textarea': {
                            verticalAlign: 'top',
                            },
                        }}
                    />
                    <div className='text-start'>
                        {noteErrorMessage && <span style={{ color: 'red', left: 10, }}>This field is required!</span>}
                    </div>
                </div>
                <div 
                    style={{ 
                        display: 'flex',
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        position: 'relative',
                        marginBlockStart: 20,
                    }}
                >
                    <Button 
                        color="primary w-25 fw-bold"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    )
};

export default CRMEntryModal;